/**
 * DraftComparison Component
 * 
 * Side-by-side comparison of Generic AI (Baseline) vs User's Replica outputs.
 * Shows issues detected, estimated edits, time, and Decision Guardian signals.
 */

import React from 'react';
import { AlertCircle, Clock, Edit3, Shield, AlertTriangle, User, Sparkles } from 'lucide-react';
import type { DraftResult } from '../../types/trainingTypes';
import { formatTime } from '../../lib/scoringUtils';

interface DraftComparisonProps {
    baseline: DraftResult;
    replica: DraftResult;
}

export const DraftComparison: React.FC<DraftComparisonProps> = ({
    baseline,
    replica
}) => {
    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* LEFT — Generic AI (Baseline) */}
            <DraftColumn
                title="Generic AI"
                subtitle="Baseline"
                draft={baseline}
                variant="baseline"
            />

            {/* RIGHT — User's Replica */}
            <DraftColumn
                title="Your Replica"
                subtitle="Specialized"
                draft={replica}
                variant="replica"
            />
        </div>
    );
};

// ============================================
// DRAFT COLUMN SUB-COMPONENT
// ============================================

interface DraftColumnProps {
    title: string;
    subtitle: string;
    draft: DraftResult;
    variant: 'baseline' | 'replica';
}

const DraftColumn: React.FC<DraftColumnProps> = ({
    title,
    subtitle,
    draft,
    variant
}) => {
    const isReplica = variant === 'replica';
    const headerClass = isReplica
        ? 'bg-hig-accent/10 border-hig-accent/30'
        : 'bg-hig-bg-tertiary border-hig-border';

    return (
        <div className="card-elevated overflow-hidden flex flex-col">
            {/* Header */}
            <div className={`px-4 py-3 border-b ${headerClass}`}>
                <div className="flex items-center justify-between">
                    <div>
                        <h3 className="font-semibold text-hig-text-primary">{title}</h3>
                        <span className="text-xs text-hig-text-tertiary uppercase tracking-wide">
                            {subtitle}
                        </span>
                    </div>
                    {isReplica && (
                        <div className="flex items-center gap-1.5 text-hig-accent">
                            <Shield className="w-4 h-4" />
                            <span className="text-xs font-medium">Guardian Active</span>
                        </div>
                    )}
                </div>
            </div>

            {/* Draft Content */}
            <div className="flex-1 p-4 space-y-4">
                <div className="bg-hig-bg-secondary rounded-lg p-4 border border-hig-border max-h-[280px] overflow-y-auto">
                    <pre className="text-sm text-hig-text-primary whitespace-pre-wrap font-sans leading-relaxed">
                        {draft.text}
                    </pre>
                </div>

                {/* Issues Detected */}
                {draft.issuesDetected.length > 0 ? (
                    <div className="space-y-2">
                        <div className="flex items-center gap-2 text-yellow-500">
                            <AlertCircle className="w-4 h-4" />
                            <span className="text-xs font-semibold uppercase tracking-wide">
                                Issues Detected ({draft.issuesDetected.length})
                            </span>
                        </div>
                        <ul className="space-y-1.5">
                            {draft.issuesDetected.map((issue, idx) => (
                                <li
                                    key={idx}
                                    className="text-sm text-hig-text-secondary flex items-start gap-2"
                                >
                                    <span className="text-yellow-500 mt-0.5">•</span>
                                    {issue}
                                </li>
                            ))}
                        </ul>
                    </div>
                ) : (
                    <div className="flex items-center gap-2 text-green-500">
                        <Shield className="w-4 h-4" />
                        <span className="text-sm font-medium">No issues detected</span>
                    </div>
                )}

                {/* Metrics */}
                <div className="flex gap-4 pt-2 border-t border-hig-divider">
                    <MetricBlock
                        icon={<Edit3 className="w-3.5 h-3.5" />}
                        label="Est. Edits"
                        value={draft.estimatedEdits.toString()}
                        variant={draft.estimatedEdits === 0 ? 'success' : 'default'}
                    />
                    <MetricBlock
                        icon={<Clock className="w-3.5 h-3.5" />}
                        label="Est. Time"
                        value={formatTime(draft.estimatedTimeSeconds)}
                        variant={draft.estimatedTimeSeconds === 0 ? 'success' : 'default'}
                    />
                </div>

                {/* Decision Guardian Signals (Replica only) */}
                {isReplica && (
                    <div className="space-y-2 pt-2 border-t border-hig-divider">
                        <span className="text-xs font-semibold text-hig-text-tertiary uppercase tracking-wide">
                            Decision Guardian
                        </span>

                        {/* Requires Approval */}
                        {draft.guardianSignals.requiresApproval && (
                            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-hig-accent/10 border border-hig-accent/30">
                                <User className="w-4 h-4 text-hig-accent" />
                                <span className="text-sm font-medium text-hig-accent">
                                    Requires Human Approval
                                </span>
                            </div>
                        )}

                        {/* Escalation Triggered */}
                        {draft.guardianSignals.escalationTriggered && (
                            <div className="px-3 py-2 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
                                <div className="flex items-center gap-2">
                                    <AlertTriangle className="w-4 h-4 text-yellow-400" />
                                    <span className="text-sm font-medium text-yellow-400">
                                        Escalation Triggered
                                    </span>
                                </div>
                                {draft.guardianSignals.escalationReason && (
                                    <p className="text-xs text-yellow-400/80 mt-1 ml-6">
                                        {draft.guardianSignals.escalationReason}
                                    </p>
                                )}
                            </div>
                        )}

                        {/* No signals */}
                        {!draft.guardianSignals.requiresApproval && !draft.guardianSignals.escalationTriggered && (
                            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-green-500/10 border border-green-500/30">
                                <Shield className="w-4 h-4 text-green-400" />
                                <span className="text-sm font-medium text-green-400">
                                    No escalation required
                                </span>
                            </div>
                        )}

                        {/* Titans Surprise Detection */}
                        {draft.guardianSignals.surpriseScore !== undefined && (
                            <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-purple-500/10 border border-purple-500/30">
                                <div className="flex items-center gap-2">
                                    <Sparkles className="w-4 h-4 text-purple-400" />
                                    <span className="text-sm font-medium text-purple-400">
                                        Surprise Detection
                                    </span>
                                </div>
                                <div className="flex items-center gap-3 text-xs">
                                    <span className="text-purple-300">
                                        Score: <strong>{draft.guardianSignals.surpriseScore}</strong>/100
                                    </span>
                                    {draft.guardianSignals.confidenceLevel !== undefined && (
                                        <span className="text-hig-accent">
                                            Conf: <strong>{Math.round(draft.guardianSignals.confidenceLevel * 100)}%</strong>
                                        </span>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

// ============================================
// METRIC BLOCK SUB-COMPONENT
// ============================================

interface MetricBlockProps {
    icon: React.ReactNode;
    label: string;
    value: string;
    variant?: 'default' | 'success';
}

const MetricBlock: React.FC<MetricBlockProps> = ({
    icon,
    label,
    value,
    variant = 'default'
}) => {
    const valueClass = variant === 'success'
        ? 'text-green-400'
        : 'text-hig-text-primary';

    return (
        <div className="flex items-center gap-2">
            <span className="text-hig-text-tertiary">{icon}</span>
            <span className="text-xs text-hig-text-tertiary uppercase tracking-wide">{label}:</span>
            <span className={`text-sm font-semibold ${valueClass}`}>{value}</span>
        </div>
    );
};

export default DraftComparison;
